import {
  defaultStagedThreshold,
  defaultTestInterval,
  type IEventCancellation,
  lapsedMilliseconds
} from './staged-event.js';
import { getApiToken, getCurrentUser } from '../api/current-user.js';
import { globalState } from '../ui/global-state.js';
import { debounce } from '../common/helpers/callbacks';
import { headerServiceRestarting } from '../api/network-consts.js';
import { showServiceRestartingNotification } from '../api/service-restart-handler.js';

export type EventSourceHandler = (data: any, eventName: string) => void;
export interface IStagedExecutionOptions {
  event: () => void | Promise<void>;
  threshold?: number;
  title?: string;
  eventTriggered?: EventSourceHandler;
  eventFinally?: () => void;
  eventActioned?: EventSourceHandler;
  cancelToken?: IEventCancellation;
  testInterval?: number;
}

/**
 * stagedExecution
 *
 * This function creates an event handler that batches incoming events and executes a main action after a specified time threshold.
 * It is useful when you want to avoid running an expensive operation for every event, and instead process them in batches.
 *
 * Usage scenario: Receiving many events in a short time, but only needing to act once after a pause.
 *
 * @param options - IStagedExecutionOptions
 *   event: The main function to execute after the threshold (can be async)
 *   threshold: Time in ms to wait before executing the main event (default: defaultStagedThreshold)
 *   title: Optional name for logging
 *   eventTriggered: Called when the first event is received
 *   eventFinally: Called after the main event finishes
 *   eventActioned: Called for every event received
 *   cancelToken: Optional cancellation support
 *   testInterval: How often to check if threshold is reached (default: defaultTestInterval)
 *
 * Returns: An EventSourceHandler function to be used as an event listener
 */
export function stagedExecution(options: IStagedExecutionOptions): EventSourceHandler {
  // Interval to check if threshold is reached
  const intervalms = options.testInterval ?? defaultTestInterval;
  let timer: NodeJS.Timeout | undefined = undefined;
  let triggered: Date | undefined = undefined; // When first event was received
  let actioned: Date | undefined = undefined; // When last event was received
  let actionCount = 0; // Number of events received
  let eventActionQueue: { data: any; eventName: string }[] = []; // Events received while executing
  let executing = false; // Is the main event running?
  let restart = false; // Should we restart after execution?

  /**
   * Resets the staged execution state and timer
   */
  const reset = () => {
    clearInterval(timer);
    timer = undefined;
    triggered = undefined;
    actionCount = 0;
    actioned = undefined;
  };

  /**
   * Checks if the threshold is reached and runs the main event
   */
  const intervalEvent = async () => {
    // If cancelled, reset and call finally
    if (options.cancelToken?.cancelled) {
      reset();
      options.cancelToken?.reset();
      options.eventFinally?.();
      return;
    }
    // How long since first event?
    const ms = lapsedMilliseconds(triggered!);
    if (ms >= (options.threshold ?? defaultStagedThreshold)) {
      try {
        executing = true;
        await options.event(); // Run main event
        options.eventFinally?.();
      } finally {
        executing = false;
        reset();
        // If new events arrived while executing, process them
        if (restart) {
          try {
            // Call eventTriggered for the first queued event
            options.eventTriggered?.(eventActionQueue[0].data, eventActionQueue[0].eventName);
            // Call eventActioned for all queued events
            eventActionQueue
              .filter((_x, index) => index > 0)
              .forEach(x => {
                options.eventActioned?.(x.data, x.eventName);
              });
          } finally {
            eventActionQueue = [];
          }
          // Start the process again
          startProcess();
        }
      }
      return;
    }
    // Not ready yet, check again after interval
    timer = setTimeout(intervalEvent, intervalms);
  };

  /**
   * The returned event handler function
   * Receives (data, eventName) for each event
   */
  return (data: any, eventName: string) => {
    console.log(`staged ${eventName} with ${data}`);
    if (!triggered) {
      // First event received
      options.eventTriggered?.(data, eventName);
      options.eventActioned?.(data, eventName);
      startProcess();
    } else {
      // Subsequent events
      if (executing) restart = true; // If main event is running, queue restart
      actionCount++;
      actioned = new Date();
      if (executing)
        eventActionQueue.push({ data, eventName }); // Queue event if busy
      else options.eventActioned?.(data, eventName); // Otherwise, call actioned immediately
      console.log(
        `Triggered Event '${options.title ?? 'no name'}' actioned ${actionCount} since ${triggered.toLocaleTimeString()}, at ${actioned.toLocaleTimeString()}`
      );
    }
  };

  /**
   * Starts the staged execution timer and state
   */
  function startProcess() {
    executing = false;
    restart = false;
    options.cancelToken?.reset();
    triggered = new Date();
    actionCount++;
    actioned = triggered;
    timer = setTimeout(intervalEvent, intervalms);
    console.log(`Triggered Event '${options.title ?? 'no name'}' started ${triggered.toLocaleTimeString()}`);
  }
}
export interface WMEventSourceClientInitializer {
  /**
   * callback to indicate online offline status
   * @param value state of the instance
   * @returns
   */
  onlineCallback: (value: boolean) => void;
  afterOnlineEvent: (() => Promise<void>) | (() => void);
  url: string;
  /**
   * Optional callback when service restart is detected
   * If not provided, will show the default service restart notification
   */
  onServiceRestarting?: () => void;
}

(function () {
  globalState().wmEventSourceClients = globalState().wmEventSourceClients ?? {};
})();

const _all_ = '__all__';

/**
 * Reconnection state information exposed by the event source client
 */
export interface ReconnectionState {
  /** Number of reconnection attempts made */
  attemptCount: number;
  /** Current backoff delay in seconds before next attempt */
  nextRetrySeconds: number;
  /** Whether a reconnection attempt is currently in progress */
  isReconnecting: boolean;
  /** Timestamp of the last reconnection attempt */
  lastAttemptTime?: Date;
  /** Timestamp when the next retry is scheduled to occur */
  nextRetryScheduledTime?: Date;
}

/** Callback type for reconnection state changes */
export type ReconnectionStateListener = (state: ReconnectionState) => void;

export class WMEventSourceClient {
  private _initializer: WMEventSourceClientInitializer;
  private static _instances(): { [key: string]: WMEventSourceClient } {
    return globalState().wmEventSourceClients as { [key: string]: WMEventSourceClient };
  }
  userId: string = '00000000-0000-0000-0000-000000000000';

  private _reconnectionAttemptCount = 0;
  private _isReconnecting = false;
  private _lastAttemptTime?: Date;
  private _nextRetryScheduledTime?: Date;
  private _reconnectionStateListeners: ReconnectionStateListener[] = [];

  /**
   * Get current reconnection state
   */
  public get reconnectionState(): ReconnectionState {
    return {
      attemptCount: this._reconnectionAttemptCount,
      nextRetrySeconds: this.reconnectFrequencySeconds,
      isReconnecting: this._isReconnecting,
      lastAttemptTime: this._lastAttemptTime,
      nextRetryScheduledTime: this._nextRetryScheduledTime
    };
  }

  /**
   * Subscribe to reconnection state changes
   * @returns Unsubscribe function
   */
  public onReconnectionStateChange(listener: ReconnectionStateListener): () => void {
    this._reconnectionStateListeners.push(listener);
    return () => {
      this._reconnectionStateListeners = this._reconnectionStateListeners.filter(l => l !== listener);
    };
  }

  private _notifyReconnectionStateChange(): void {
    const state = this.reconnectionState;
    this._reconnectionStateListeners.forEach(listener => listener(state));
  }
  public static getInstance(
    instanceName?: string,
    factoryOptions?: WMEventSourceClientInitializer
  ): WMEventSourceClient {
    if (!WMEventSourceClient._instances()[instanceName ?? 'default']) {
      if (!factoryOptions) throw new Error(`WMEventSourceClient.getInstance requires factoryOptions on first call`);
      WMEventSourceClient._instances()[instanceName ?? 'default'] = new WMEventSourceClient(factoryOptions);
    }
    return WMEventSourceClient._instances()[instanceName ?? 'default'];
  }
  private _source?: EventSource;
  private _events: { [key: string]: EventSourceHandler[] } = {};
  constructor(factoryOptions: WMEventSourceClientInitializer) {
    this._initializer = factoryOptions;
    this.addEventListener('ping', () => this._ping());
  }

  reconnectFrequencySeconds = 1;

  private _reconnectInternal = debounce(
    () => {
      this._reconnectionAttemptCount++;
      this._lastAttemptTime = new Date();
      this._isReconnecting = true;
      this._nextRetryScheduledTime = undefined; // Clear scheduled time when attempt starts
      this._notifyReconnectionStateChange();

      this.connectUser();
      // Double every attempt to avoid overwhelming server
      this.reconnectFrequencySeconds *= 2;
      // Max out at ~1 minute as a compromise between user experience and server load
      if (this.reconnectFrequencySeconds >= 64) {
        this.reconnectFrequencySeconds = 64;
      }
    },
    () => this.reconnectFrequencySeconds * 1000
  );

  /**
   * Schedule a reconnection attempt. Notifies listeners immediately about the scheduled retry.
   */
  private _reconnect() {
    // Calculate and store when the next retry will happen
    this._nextRetryScheduledTime = new Date(Date.now() + this.reconnectFrequencySeconds * 1000);
    this._isReconnecting = false; // Not reconnecting yet, waiting for scheduled time
    this._notifyReconnectionStateChange();

    // Actually schedule the reconnect
    this._reconnectInternal();
  }

  public static pushEventToDefault(eventName: string, data: any) {
    WMEventSourceClient.getInstance().pushEvent(eventName, data);
  }
  public pushEvent(eventName: string, data: any) {
    this._events[eventName]?.forEach(event => {
      event(data, eventName);
    });
    this._events[_all_]?.forEach(event => {
      event(data, eventName);
    });
  }
  connectUser() {
    this.disconnect();

    const user = getCurrentUser();
    if (!user) throw new Error('User not connected');
    const _token = getApiToken();
    const token = encodeURIComponent(_token);
    const sourceUrl = `${this._initializer.url}/api/system/sse?&token=${token}`;

    // Pre-check: make a fetch to detect if service is restarting (503 with header)
    // EventSource API doesn't expose HTTP status codes, so we need this workaround
    this._checkServiceAvailability().then(isAvailable => {
      if (!isAvailable) {
        // Service is restarting or unavailable, don't open EventSource yet
        // The reconnect logic will handle retrying
        this._reconnect();
        return;
      }

      this._source = new EventSource(sourceUrl);
      this._source.onopen = () => {
        this._initializer.onlineCallback(true);
        this.reconnectFrequencySeconds = 1; // Reset backoff on successful connection
        this._reconnectionAttemptCount = 0;
        this._isReconnecting = false;
        this._nextRetryScheduledTime = undefined; // Clear scheduled retry on success
        this._notifyReconnectionStateChange();
        this._initializer.afterOnlineEvent?.();
      };
      this._source.onmessage = event => {
        const data = JSON.parse(event.data);

        const eventName = data.eventName;
        this._events[eventName]?.forEach(event1 => {
          event1(data, eventName);
        });
        this._events[_all_]?.forEach(event1 => {
          event1(data, eventName);
        });
      };
      this._source.onerror = _err => {
        console.log(_err);
        this._initializer.onlineCallback(false);
        this._source?.close();
        this._source = undefined;
        this._reconnect();
      };
    });
  }

  /**
   * Checks if the service is available before opening EventSource.
   * This allows us to detect 503 service restart responses that EventSource can't see.
   * @returns true if service is available, false if restarting or unavailable
   */
  private async _checkServiceAvailability(): Promise<boolean> {
    try {
      const response = await fetch(`${this._initializer.url}/api/system/health`, {
        method: 'GET',
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000)
      });

      // Check for service restart (503 with special header)
      if (response.status === 503 && response.headers.get(headerServiceRestarting) === 'true') {
        console.log('EventSource: Service is restarting, will retry...');
        if (this._initializer.onServiceRestarting) {
          this._initializer.onServiceRestarting();
        } else {
          showServiceRestartingNotification();
        }
        this._initializer.onlineCallback(false);
        return false;
      }

      // Any successful response means service is available
      return response.ok;
    } catch (error) {
      console.log('EventSource: Service availability check failed', error);
      this._initializer.onlineCallback(false);
      return false;
    }
  }

  private _ping = () => {
    console.log('SourceEvent Server Ping');
  };
  disconnect() {
    this._source?.close();
    this._source = undefined;
  }
  addEventListener(eventNames: string | string[], event: EventSourceHandler) {
    const events = Array.isArray(eventNames) ? eventNames : [eventNames];

    events.forEach(_eventName => {
      const eventName = _eventName === '' ? _all_ : _eventName;

      let eventArray: EventSourceHandler[] = this._events[eventName];
      if (!eventArray) {
        eventArray = [];
        this._events[eventName] = eventArray;
      }
      if (!eventArray.find(x => x === event)) eventArray.push(event);
    });
  }
  removeEventListener(eventNames: string | string[], event: EventSourceHandler) {
    const events = Array.isArray(eventNames) ? eventNames : [eventNames];
    events.forEach(_eventName => {
      const eventName = _eventName === '' ? _all_ : _eventName;
      const eventArray: EventSourceHandler[] = this._events[eventName];
      this._events[eventName] = eventArray?.filter(binder => !(binder === event));
    });
  }
}
